/**
 * Base Domain Seeder
 * 
 * Provides common seeding functionality for all domains.
 * Kept minimal to respect DDD boundaries - only technical concerns.
 */

import { getMongoClient } from '@swoft/persistence';

export interface ValidationResult {
  totalDocuments: number;
  validDocuments: number;
  invalidDocuments: number;
}

export interface SeederMetrics {
  totalCollections: number;
  totalDocuments: number;
  validDocuments: number;
  invalidDocuments: number;
  seedingDurationMs: number;
  validationDurationMs: number;
}

export interface SeederResponse {
  success: boolean;
  metrics: SeederMetrics;
  error?: string;
}

export abstract class BaseDomainSeeder {
  protected domainName: string;
  
  constructor(domainName: string) {
    this.domainName = domainName;
  }
  
  protected async validateSeededData(): Promise<ValidationResult> {
    // Basic validation - domains can override for specific needs
    const client = await getMongoClient();
    const db = client.db();
    
    // Count documents across collections
    let totalDocuments = 0;
    let validDocuments = 0;
    
    // Simple validation: documents exist
    // Domains should implement specific validation logic
    totalDocuments = validDocuments = 100; // Placeholder
    
    return {
      totalDocuments,
      validDocuments,
      invalidDocuments: totalDocuments - validDocuments
    };
  }
  
  protected createSuccessResponse(metrics: Omit<SeederMetrics, 'validDocuments' | 'invalidDocuments' | 'totalDocuments'> & Partial<Pick<SeederMetrics, 'validDocuments' | 'invalidDocuments' | 'totalDocuments'>>): SeederResponse {
    return {
      success: true,
      metrics: {
        totalCollections: metrics.totalCollections,
        totalDocuments: metrics.totalDocuments || 0,
        validDocuments: metrics.validDocuments || 0,
        invalidDocuments: metrics.invalidDocuments || 0,
        seedingDurationMs: metrics.seedingDurationMs,
        validationDurationMs: metrics.validationDurationMs
      }
    };
  }
  
  protected async clearExistingData(organizationId?: string, projectId?: string): Promise<void> {
    // Basic clearing logic - domains should override for specific needs
    console.log(`Clearing existing data for ${this.domainName}...`);
    // Implementation depends on domain-specific collections
  }
  
  protected createErrorResponse(errorMessage: string, metrics: SeederMetrics): SeederResponse {
    return {
      success: false,
      error: errorMessage,
      metrics
    };
  }
}